import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Tag, Sparkles, Wand2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

export default function Prices() {
  const { company, reloadCompany } = useOutletContext();
  const [notes, setNotes] = useState(company?.pricing_notes || "");
  const [rows, setRows] = useState(() => (company?.price_list?.length ? company.price_list.map((p) => ({ service: p.service || "", price: p.price ?? "" })) : [{ service: "", price: "" }]));
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState("");

  const update = (i, k, v) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const addRow = () => setRows((r) => [...r, { service: "", price: "" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const runPreview = async () => {
    if (!notes.trim()) { toast.error("Write your pricing first, then preview"); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/company/pricing-preview", { pricing_notes: notes });
      setPreview(data.summary || "Ava couldn't pick anything out — try adding a bit more detail.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't generate a preview"); }
    finally { setPreviewing(false); }
  };

  const save = async () => {
    const clean = rows
      .filter((r) => r.service.trim() !== "" && r.price !== "" && !isNaN(Number(r.price)))
      .map((r) => ({ service: r.service.trim(), price: Number(r.price) }));
    setSaving(true);
    try {
      await api.patch("/company/me", { pricing_notes: notes.trim() || null, price_list: clean });
      await reloadCompany?.();
      toast.success("Pricing saved — Ava will quote rough estimates from this");
      setRows(clean.length ? clean.map((p) => ({ service: p.service, price: p.price })) : [{ service: "", price: "" }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="prices-page">
      <div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">MY PRICES</h1>
        <p className="text-muted-foreground mt-1">Just explain how you price in plain English — ranges, "from" prices, what changes a quote. Ava reads it and gives your customers a helpful rough ballpark, always making clear the exact price is confirmed on your callback.</p>
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground px-1">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> How you price (plain English)
        </div>
        <Textarea
          data-testid="pricing-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={"Type it however you'd explain it to a customer, for example:\n\nWindow spraying is usually £80 per window, up to £300 for a big bay window. Some jobs need scaffold hire and location affects travel. Doors £180–£350 depending on style. Bi-folds £400–£850. Kitchens vary a lot: small £850–£1,300, large £1,850–£4,000 depending on surfaces, doors and any repairs."}
          className="rounded-sm bg-[#0f0f11] border-white/10 min-h-[200px]"
        />
        <p className="text-xs text-muted-foreground">Mention ranges and the things that change the price (access, travel, size, materials, complexity). Ava handles the rest and never commits you to an exact figure.</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button data-testid="pricing-preview-btn" onClick={runPreview} disabled={previewing} variant="outline" className="rounded-sm border-white/10 gap-2">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Preview what Ava understood
          </Button>
        </div>
        {preview && (
          <div data-testid="pricing-preview-output" className="mt-2 bg-[#0f0f11] border border-primary/20 rounded-sm p-4 text-sm text-foreground whitespace-pre-wrap">
            <div className="text-xs uppercase tracking-wider text-primary mb-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Here's what Ava picked out</div>
            {preview}
          </div>
        )}
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground px-1">
          <Tag className="h-3.5 w-3.5" /> Or set fixed prices (optional)
        </div>
        <p className="text-xs text-muted-foreground px-1 -mt-1">For services with a flat price. Leave blank if the plain-English pricing above covers everything.</p>
        {rows.map((row, i) => (
          <div key={i} className="flex items-end gap-2" data-testid={`price-row-${i}`}>
            <div className="flex-1">
              {i === 0 && <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Service</Label>}
              <Input data-testid={`price-service-${i}`} value={row.service} onChange={(e) => update(i, "service", e.target.value)} placeholder="e.g. Boiler service" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div className="w-32">
              {i === 0 && <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Price (£)</Label>}
              <Input data-testid={`price-amount-${i}`} type="number" inputMode="decimal" min="0" value={row.price} onChange={(e) => update(i, "price", e.target.value)} placeholder="0" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <Button data-testid={`price-remove-${i}`} onClick={() => removeRow(i)} variant="ghost" size="icon" className="rounded-sm text-muted-foreground hover:text-red-400 shrink-0"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button data-testid="add-price-row" onClick={addRow} variant="outline" className="rounded-sm border-white/10 gap-2 mt-2"><Plus className="h-4 w-4" /> Add another price</Button>
      </div>

      <Button data-testid="save-prices-btn" onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-orange-600 text-white gap-2">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save pricing"}
      </Button>
    </div>
  );
}
